/**
 * Models panel — one list of what exists, with the tick to enable.
 *
 * Replaces the old Costs registry and the hand-kept provider rows:
 *  - Rows come from the engine's routing read (`/settings/llm/routing`), the
 *    router's live contract: every model, label, provider and quality tier is
 *    engine-driven, so adding a model in the engine shows up here with no
 *    Seitiate+ release. Nothing is bundled with the app.
 *  - The tick reads and writes the tenant's enabled-models registry on the
 *    engine (MODEL-REGISTRY-PER-TENANT-1).
 *  - Per-token prices are honest "Coming soon": the router's contracts do not
 *    serve a per-model price read over HTTP yet (the model table is
 *    engine-internal and its host-selection endpoint's switch is OFF), and the
 *    packet sends prices through the router — so no number is rendered from
 *    the Seitiate+ catalog or a bundled list (the 2026-09-07 drift).
 *
 * Reads: the routing read and the enabled-models read (both engine). Both are
 * time-limited via useRouterRead and the abort signal reaches fetch, so the
 * budget cancels the wire request; a failure shows in this panel only.
 */

import React, { useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import {
  getEnabledModels,
  getLLMTaskMappings,
  setEnabledModels,
  type EnabledModelEntry,
  type LLMModelOption,
  type LLMTaskMappingsResponse,
} from '@/api/llmSettings';
import { ENABLED_MODELS_SWR_KEY } from '@/components/settings/panels/useEnabledModels';
import { ComingSoonBlock, RouterReadError, RouterReadLoading } from './PanelErrorBoundary';
import { ROUTING_SWR_KEY } from './RoutingPanel';
import { useRouterRead } from './useRouterRead';

function samePair(entry: EnabledModelEntry, providerId: string, modelId: string): boolean {
  return entry.provider_id === providerId && entry.model_id === modelId;
}

export const ModelsPanel: React.FC = () => {
  const { mutate } = useSWRConfig();
  const routing = useRouterRead<LLMTaskMappingsResponse>(ROUTING_SWR_KEY, (signal) =>
    getLLMTaskMappings({ signal }),
  );
  const enabled = useRouterRead(ENABLED_MODELS_SWR_KEY, (signal) =>
    // The signal reaches fetch, so the useRouterRead time budget cancels the
    // wire request instead of letting it settle unseen.
    getEnabledModels({ signal }),
  );

  const [search, setSearch] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const rows = useMemo(() => {
    const options: LLMModelOption[] = Array.isArray(routing.data?.options) ? routing.data.options : [];
    if (!search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter((option) =>
      `${option.label} ${option.model_id} ${option.provider_id} ${option.quality_tier}`
        .toLowerCase()
        .includes(needle),
    );
  }, [routing.data, search]);

  const enabledSet = useMemo(() => {
    const set = new Set<string>();
    for (const entry of enabled.data?.enabled ?? []) {
      set.add(`${entry.provider_id}::${entry.model_id}`);
    }
    return set;
  }, [enabled.data]);

  const handleToggle = async (option: LLMModelOption) => {
    const current = enabled.data?.enabled ?? [];
    const key = `${option.provider_id}::${option.model_id}`;
    const isEnabled = enabledSet.has(key);
    const next: EnabledModelEntry[] = isEnabled
      ? current.filter((pair) => !samePair(pair, option.provider_id, option.model_id))
      : [...current, { provider_id: option.provider_id, model_id: option.model_id }];
    setBusyKey(key);
    setActionMessage(null);
    try {
      await setEnabledModels(next);
      await mutate(ENABLED_MODELS_SWR_KEY);
      setActionMessage(
        `${option.label} ${isEnabled ? 'removed from' : 'added to'} your active model set.`,
      );
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : 'Could not update the model set.');
    } finally {
      setBusyKey(null);
    }
  };

  if (routing.error) {
    return <RouterReadError error={routing.error} onRetry={routing.retry} />;
  }
  if (routing.isLoading) {
    return <RouterReadLoading label="Loading the model list…" />;
  }

  const enabledStateKnown = !enabled.error;

  return (
    <div data-testid="models-routing-models-panel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-3)' }}>
      <div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
          Models &amp; Routing
        </div>
        <h2 style={{ margin: '6px 0 4px', fontSize: 20, fontWeight: 600, color: 'var(--sp-text)' }}>Models</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--sp-text-muted)', maxWidth: 720 }}>
          What exists and the tick that enables a model for this workspace, read from the
          engine&apos;s routing contract; nothing here is bundled with the app. Data terms for
          each provider live in{' '}
          <a href="/settings/keys" style={{ color: 'var(--sp-accent)' }}>Keys and accounts</a>, and
          the advisory terms for every option render with the option list in{' '}
          <a href="/settings/routing" style={{ color: 'var(--sp-accent)' }}>Routing</a>.
        </p>
      </div>

      {!enabledStateKnown ? (
        <div
          data-testid="mr-enabled-unavailable"
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            padding: 'var(--sp-space-3)',
            fontSize: 12,
            color: 'var(--sp-text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ flex: 1 }}>
            Enabled state could not be read, so the ticks are paused. The list is unaffected.
          </span>
          <button type="button" className="sp-btn" onClick={enabled.retry}>
            Retry
          </button>
        </div>
      ) : null}

      {actionMessage ? (
        <div
          role="status"
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            padding: 'var(--sp-space-3)',
            fontSize: 12,
            color: 'var(--sp-text)',
          }}
        >
          {actionMessage}
        </div>
      ) : null}

      <input
        type="search"
        className="sp-input"
        placeholder="Search models or providers…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        autoComplete="off"
        name="models-routing-search"
        data-form-type="other"
        data-1p-ignore
        data-lpignore="true"
        style={{ maxWidth: 320 }}
      />

      {rows.length === 0 ? (
        <div className="sp-empty" style={{ padding: '24px 12px' }}>
          {search ? 'No models match this search.' : 'The engine is not advertising any models yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((option) => {
            const key = `${option.provider_id}::${option.model_id}`;
            const isEnabled = enabledSet.has(key);
            const busy = busyKey === key;
            const name = option.label;
            return (
              <div
                key={key}
                data-testid={`mr-model-row-${option.model_id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 'var(--sp-space-3)',
                  border: '1px solid var(--sp-border)',
                  borderRadius: 'var(--sp-radius-lg)',
                  background: 'var(--sp-bg-elev)',
                  padding: '10px var(--sp-space-3)',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    disabled={!enabledStateKnown || busy}
                    aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${name}`}
                    onChange={() => void handleToggle(option)}
                  />
                  <span className="mono" style={{ fontSize: 10, color: isEnabled ? 'var(--sp-accent)' : 'var(--sp-text-dim)' }}>
                    {busy ? 'Saving' : isEnabled ? 'Enabled' : 'Available'}
                  </span>
                </label>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>{name}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                    {option.provider_id}
                    {option.quality_tier ? ` · ${option.quality_tier}` : ''}
                    {option.data_policy?.region ? ` · ${option.data_policy.region}` : ''}
                  </div>
                </div>
                <div
                  className="mono"
                  data-testid={`mr-model-price-${option.model_id}`}
                  aria-disabled="true"
                  style={{ fontSize: 10, color: 'var(--sp-text-dim)', whiteSpace: 'nowrap', opacity: 0.6 }}
                >
                  Coming soon
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ComingSoonBlock
        title="Per-token prices on this list"
        body="Prices arrive with the router's model-table price read. Until then no number is shown here at all — never one bundled with the app, and never one from a table that can drift from the engine."
        testId="mr-models-prices-coming-soon"
      />
    </div>
  );
};
